/**
 * TERRAIN (owner B2): the ornament. Contours and the ridge (spec §D.6) and the hillshade.
 *
 *   contourSourceSpec(geo.contours)          GeoJSON source SOURCE_IDS.contours (in the built style)
 *   contourLayers(mood, { layout, visible })  ld-contours-minor / -major / ld-ridge / ld-contours-label,
 *                                             placed just beneath ANCHORS.terrain by the style builder
 *   createTerrain(map, …)                     runtime: hillshade on the DEM (added on "dem:ready"),
 *                                             layer-flag fades, mood tweens, the intro's contour fade
 *   fadeContours(map, v, { duration, ease })  the intro beat (§E.7): contours 0 → 1 over 2400 drift
 *
 * Motion: every value here that moves is written by the ticker with the brand curve (setPaintProperty
 * per frame on data-constant properties: one uniform each). Colours ride MapLibre paint transitions.
 *   - contours flag: line-layer-opacity 0 ↔ 1 (in 640 settle, out 448 leave); overlapping lines never add up
 *   - hillshade: exaggeration 0 → mood value over 1400 drift when the DEM arrives (light arriving);
 *     the flag fades it (640 settle / 448 leave) and then hides the layer
 *   - mood: illumination direction (not transitionable) and exaggeration tween over the mood duration with
 *     MapLibre's own cubic in-out, so they move in step with the colour transitions (§E.8)
 */
import type {
  ExpressionSpecification,
  FilterSpecification,
  GeoJSONSourceSpecification,
  LayerSpecification,
  Map as MLMap,
} from "maplibre-gl";
import type { ContourCollection } from "@/contracts/data";
import { ANCHORS, LAYER_IDS, SOURCE_IDS, type EaseName } from "@/contracts/map";
import type { Layout, Mood } from "@/contracts/store";
import { easeBy } from "@/design";
import { ticker } from "@/lib/ticker";
import { mixRGBA, parseColor } from "@/map/gl/color";
import { REGION_BBOX } from "@/contracts/taxonomy";
import { demSource, prewarmDem, setDemUpsample } from "@/map/memorySource";
import { prewarmTiles } from "@/map/prewarm";
import { FONTSTACKS, MOODS } from "@/map/style/moods";
import { on } from "@/store/bus";
import { getApp } from "@/store/store";
import { textScale } from "./style/flavors";

/* ─── Static style parts ─────────────────────────────────────────────────── */

export const CONTOUR_LINE_LAYERS = [LAYER_IDS.contoursMinor, LAYER_IDS.contoursMajor, LAYER_IDS.ridge] as const;
export const CONTOUR_LAYERS = [...CONTOUR_LINE_LAYERS, LAYER_IDS.contoursLabel] as const;

const EMPTY_FC = { type: "FeatureCollection", features: [] } as const;

/** The contours as a GeoJSON source. geojson-vt tiles to z15; the ridge stays smooth at z16 by overzoom. */
export function contourSourceSpec(data: ContourCollection | null | undefined): GeoJSONSourceSpecification {
  return {
    type: "geojson",
    data: (data ?? EMPTY_FC) as unknown as GeoJSONSourceSpecification["data"],
    maxzoom: 15,
    tolerance: 0.375,
    buffer: 64,
  };
}

const IS_REGION: ExpressionSpecification = ["==", ["get", "lod"], "region"];
const byLod = (region: number, metro: number): ExpressionSpecification | number =>
  region === metro ? region : (["case", IS_REGION, region, metro] as ExpressionSpecification);

/** §D.6 opacity expressions (validated by S5; a data expression inside a zoom interpolate). */
export const CONTOUR_OPACITY = {
  ridge: ["interpolate", ["linear"], ["zoom"], 8.6, 0, 9.2, 1] as ExpressionSpecification,
  major: [
    "interpolate", ["linear"], ["zoom"],
    8.6, 0,
    9.2, byLod(1, 0),
    11.5, byLod(1, 0),
    12.5, byLod(0, 1),
  ] as ExpressionSpecification,
  minor: [
    "interpolate", ["linear"], ["zoom"],
    9.6, 0,
    10.6, byLod(1, 0),
    11.5, byLod(1, 0),
    12.5, 0,
    13.5, byLod(0, 1),
  ] as ExpressionSpecification,
} as const;

export const CONTOUR_FILTERS = {
  ridge: ["==", ["get", "ridge"], true] as FilterSpecification,
  major: ["all", ["get", "major"], ["!=", ["get", "ridge"], true]] as FilterSpecification,
  minor: ["!", ["get", "major"]] as FilterSpecification,
  label: ["all", ["==", ["get", "lod"], "metro"], ["get", "major"]] as FilterSpecification,
} as const;

const NO_TRANSITION = { duration: 0, delay: 0 };

/** The four contour layers (bottom → top: minor, major, ridge, label), coloured for `mood`. */
export function contourLayers(mood: Mood, o: { layout?: Layout; visible?: boolean } = {}): LayerSpecification[] {
  const c = MOODS[mood].contours;
  const { k, halo } = textScale(o.layout);
  const shown = o.visible === false ? 0 : 1;
  const lineLayout = { "line-join": "round", "line-cap": "round" } as const;
  return [
    {
      id: LAYER_IDS.contoursMinor,
      type: "line",
      source: SOURCE_IDS.contours,
      filter: CONTOUR_FILTERS.minor,
      layout: lineLayout,
      paint: {
        "line-color": c.minor,
        "line-width": c.minorWidth,
        "line-opacity": CONTOUR_OPACITY.minor,
        "line-layer-opacity": shown,
        "line-layer-opacity-transition": NO_TRANSITION,
      },
    },
    {
      id: LAYER_IDS.contoursMajor,
      type: "line",
      source: SOURCE_IDS.contours,
      filter: CONTOUR_FILTERS.major,
      layout: lineLayout,
      paint: {
        "line-color": c.major,
        "line-width": ["interpolate", ["linear"], ["zoom"], 9, c.majorWidth * 0.8, 10, c.majorWidth],
        "line-opacity": CONTOUR_OPACITY.major,
        "line-layer-opacity": shown,
        "line-layer-opacity-transition": NO_TRANSITION,
      },
    },
    {
      id: LAYER_IDS.ridge,
      type: "line",
      source: SOURCE_IDS.contours,
      filter: CONTOUR_FILTERS.ridge,
      layout: lineLayout,
      paint: {
        "line-color": c.ridge,
        "line-width": c.majorWidth * 1.5,
        "line-opacity": CONTOUR_OPACITY.ridge,
        "line-layer-opacity": shown,
        "line-layer-opacity-transition": NO_TRANSITION,
      },
    },
    {
      id: LAYER_IDS.contoursLabel,
      type: "symbol",
      source: SOURCE_IDS.contours,
      filter: CONTOUR_FILTERS.label,
      minzoom: 13,
      layout: {
        "symbol-placement": "line",
        "symbol-spacing": 640,
        "text-max-angle": 25,
        "text-field": ["concat", ["to-string", ["get", "ele"]], " m"],
        "text-font": [FONTSTACKS.bold],
        "text-size": Math.round(9.5 * k * 100) / 100,
        "text-letter-spacing": 0.12,
        "text-padding": 4,
      },
      paint: {
        "text-color": c.label,
        "text-halo-color": c.halo,
        "text-halo-width": 1.5 + halo,
        "text-opacity": shown,
        "text-opacity-transition": NO_TRANSITION,
      },
    },
  ];
}

/** The hillshade layer (added at runtime once the DEM is in memory; exaggeration starts flat). */
export function hillshadeLayer(mood: Mood): LayerSpecification {
  const h = MOODS[mood].hillshade;
  return {
    id: LAYER_IDS.hillshade,
    type: "hillshade",
    source: SOURCE_IDS.dem,
    paint: {
      "hillshade-shadow-color": h.shadow,
      "hillshade-highlight-color": h.highlight,
      "hillshade-accent-color": h.accent,
      "hillshade-illumination-direction": h.illuminationDirection,
      "hillshade-illumination-anchor": "map",
      "hillshade-exaggeration": 0,
      "hillshade-exaggeration-transition": NO_TRANSITION,
    },
  };
}

/* ─── Runtime ────────────────────────────────────────────────────────────── */

/** MapLibre's style-transition curve (util.easeCubicInOut): mood tweens move in step with the colours. */
export function cubicInOut(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const t2 = t * t;
  const t3 = t2 * t;
  return 4 * (t < 0.5 ? t3 : 3 * (t - t2) + t3 - 0.75);
}

/** How often (at most) the hillshade's moving values are written while they move (L2-08: ≤ 10 Hz). */
export const HILLSHADE_STEP_MS = 125;
/** Delay before the DEM gap tiles are pre-synthesised in the worker (after the first frames). */
const PREWARM_AFTER_MS = 1500;

export interface HillshadeColors {
  shadow: string;
  highlight: string;
  accent: string;
}
export function hillshadeColors(m: Mood): HillshadeColors {
  const h = MOODS[m].hillshade;
  return { shadow: h.shadow, highlight: h.highlight, accent: h.accent };
}
const fmt = (c: [number, number, number, number]): string =>
  `rgba(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)},${Math.round(c[3] * 1000) / 1000})`;
/** The hillshade colours t of the way from a to b (straight RGBA, as MapLibre interpolates them). */
export function hillshadeMix(a: HillshadeColors, b: HillshadeColors, t: number): HillshadeColors {
  if (t <= 0) return a;
  if (t >= 1) return b;
  const mix = (x: string, y: string): string => fmt(mixRGBA(parseColor(x), parseColor(y), t));
  return { shadow: mix(a.shadow, b.shadow), highlight: mix(a.highlight, b.highlight), accent: mix(a.accent, b.accent) };
}

/** Signed shortest turn from a to b in degrees (−180, 180]. */
export function turn(a: number, b: number): number {
  const d = (((b - a) % 360) + 540) % 360 - 180;
  return d === -180 ? 180 : d;
}

interface Tw {
  from: number;
  to: number;
  t0: number;
  dur: number;
  ease: (t: number) => number;
  done?: () => void;
}

class Value {
  v: number;
  private tw: Tw | null = null;
  constructor(v: number) {
    this.v = v;
  }
  get active(): boolean {
    return this.tw !== null;
  }
  get target(): number {
    return this.tw ? this.tw.to : this.v;
  }
  set(v: number): void {
    const done = this.tw?.done;
    this.tw = null;
    this.v = v;
    done?.();
  }
  to(v: number, dur: number, ease: (t: number) => number, done?: () => void): void {
    const prev = this.tw?.done;
    this.tw = null;
    prev?.();
    if (dur <= 0 || v === this.v) {
      this.v = v;
      done?.();
      return;
    }
    this.tw = { from: this.v, to: v, t0: performance.now(), dur, ease, done };
  }
  /** Advances; true while still moving. */
  step(now: number): boolean {
    const tw = this.tw;
    if (!tw) return false;
    const t = Math.min(1, (now - tw.t0) / tw.dur);
    this.v = tw.from + (tw.to - tw.from) * tw.ease(t);
    if (t >= 1) {
      this.v = tw.to;
      this.tw = null;
      tw.done?.();
      return false;
    }
    return true;
  }
}

export interface TerrainOptions {
  mood: Mood;
  reducedMotion: () => boolean;
  contours: boolean; //  initial layer flags (the built style already carries them)
  hillshade: boolean;
}

export interface Terrain {
  readonly demAttached: boolean;
  setMood(m: Mood, durationMs: number): void;
  setContoursVisible(v: boolean): void;
  setHillshadeVisible(v: boolean): void;
  /** Multiplies the contours' layer opacity (the intro fades them 0 → 1). Resolves when settled. */
  fadeContours(v: number, o?: { duration?: number; ease?: EaseName }): Promise<boolean>;
  /** Adds the DEM source + hillshade now if the DEM is in memory (called on "dem:ready"). */
  attachDem(): void;
  destroy(): void;
}

const terrains = new WeakMap<MLMap, Terrain>();

/** Intro and choreography helper (§E.7 "contours" beat): fade every contour layer to `v` (0…1). */
export function fadeContours(map: MLMap, v: number, o?: { duration?: number; ease?: EaseName }): Promise<boolean> {
  const t = terrains.get(map);
  if (t) return t.fadeContours(v, o);
  return Promise.resolve(false);
}

export function createTerrain(map: MLMap, o: TerrainOptions): Terrain {
  let mood = o.mood;
  let destroyed = false;
  let demAttached = false;
  const reduced = (): boolean => {
    try {
      return !!o.reducedMotion();
    } catch {
      return false;
    }
  };

  // Contours: layer opacity = flag × fade.
  const cFlag = new Value(o.contours ? 1 : 0);
  const cFade = new Value(1);
  // Hillshade: exaggeration = shown × mood value; direction tweens on mood changes.
  const hShown = new Value(0);
  let hWanted = o.hillshade;
  const hEx = new Value(MOODS[mood].hillshade.exaggeration);
  const hDir = new Value(MOODS[mood].hillshade.illuminationDirection);

  // Hillshade colours: tweened here with the direction (below), not by MapLibre transitions, because
  // every write to the layer restarts all of its running transitions.
  const hCol = new Value(0); //   0 → 1 between hFrom and hTo
  let hFrom = hillshadeColors(mood);
  let hTo = hFrom;

  let lastC = -1;
  let lastEx = -1;
  let lastDir = Number.NaN;
  let lastCol = "";
  let lastHsWrite = -Infinity;

  const has = (id: string): boolean => {
    try {
      return !!map.getLayer(id);
    } catch {
      return false;
    }
  };

  function applyContours(): void {
    const v = Math.round(cFlag.v * cFade.v * 1000) / 1000;
    if (v === lastC) return;
    lastC = v;
    for (const id of CONTOUR_LINE_LAYERS) if (has(id)) map.setPaintProperty(id, "line-layer-opacity", v);
    if (has(LAYER_IDS.contoursLabel)) map.setPaintProperty(LAYER_IDS.contoursLabel, "text-opacity", v);
  }

  /**
   * Writes the hillshade's moving values in steps (L2-08): at most every HILLSHADE_STEP_MS while
   * something moves (≤ 10 Hz, never per frame: each write re-evaluates the layer's paint and restarts
   * its transitions), and at once when everything has settled. Colours and exaggeration carry a
   * transition of one step, so MapLibre glides between the writes; the direction is not
   * transitionable and turns in steps of a degree or two.
   */
  function applyHillshade(now = performance.now(), force = false): void {
    if (!demAttached || !has(LAYER_IDS.hillshade)) return;
    const moving = hShown.active || hEx.active || hDir.active || hCol.active;
    if (moving && !force && now - lastHsWrite < HILLSHADE_STEP_MS) return;
    const glide = reduced() ? NO_TRANSITION : { duration: HILLSHADE_STEP_MS, delay: 0 };
    let wrote = false;
    const cols = hillshadeMix(hFrom, hTo, hCol.v);
    const colKey = `${cols.shadow}|${cols.highlight}|${cols.accent}`;
    if (colKey !== lastCol) {
      lastCol = colKey;
      for (const k of ["shadow", "highlight", "accent"] as const) {
        map.setPaintProperty(LAYER_IDS.hillshade, `hillshade-${k}-color-transition`, glide);
        map.setPaintProperty(LAYER_IDS.hillshade, `hillshade-${k}-color`, cols[k]);
      }
      wrote = true;
    }
    const ex = Math.round(hShown.v * hEx.v * 10000) / 10000;
    if (ex !== lastEx) {
      lastEx = ex;
      map.setPaintProperty(LAYER_IDS.hillshade, "hillshade-exaggeration-transition", glide);
      map.setPaintProperty(LAYER_IDS.hillshade, "hillshade-exaggeration", ex);
      wrote = true;
    }
    const dir = Math.round((((hDir.v % 360) + 360) % 360) * 100) / 100;
    if (dir !== lastDir) {
      lastDir = dir;
      map.setPaintProperty(LAYER_IDS.hillshade, "hillshade-illumination-direction", dir);
      wrote = true;
    }
    if (wrote) lastHsWrite = now;
  }

  let loopOff: (() => void) | null = null;
  function kick(): void {
    if (loopOff || destroyed) return;
    loopOff = ticker.add((now) => {
      if (destroyed) return false;
      const a = cFlag.step(now);
      const b = cFade.step(now);
      const c = hShown.step(now);
      const d = hEx.step(now);
      const e = hDir.step(now);
      const f = hCol.step(now);
      applyContours();
      applyHillshade(now);
      const more = a || b || c || d || e || f;
      if (!more) loopOff = null;
      return more;
    });
  }
  const after = (): void => {
    applyContours();
    applyHillshade(performance.now(), true);
    if (cFlag.active || cFade.active || hShown.active || hEx.active || hDir.active || hCol.active) kick();
  };

  function setVisibility(id: string, v: boolean): void {
    if (has(id)) map.setLayoutProperty(id, "visibility", v ? "visible" : "none");
  }

  function attachDem(): void {
    if (destroyed || demAttached) return;
    if (!getApp().demReady) return;
    try {
      // Synthesised z10–12 tiles (outside METRO_BBOX) are upsampled up to 8× from z9. Bilinear leaves the
      // slope constant across each source cell, so a low western sun (285°) draws the cell grid as vertical
      // bands (rendered at Lonavala z13). Catmull-Rom keeps the slope continuous: the relief stays calm.
      setDemUpsample("bicubic");
      if (!map.getSource(SOURCE_IDS.dem)) map.addSource(SOURCE_IDS.dem, demSource());
      if (!has(LAYER_IDS.hillshade)) {
        const before = [LAYER_IDS.contoursMinor, LAYER_IDS.contoursMajor, LAYER_IDS.ridge, LAYER_IDS.contoursLabel, ANCHORS.terrain].find(has);
        const layer = hillshadeLayer(mood);
        if (!hWanted) (layer as { layout?: Record<string, unknown> }).layout = { visibility: "none" };
        map.addLayer(layer, before);
      }
    } catch (err) {
      console.error("[terrain] could not add the hillshade", err);
      return;
    }
    demAttached = true;
    lastEx = -1;
    lastDir = Number.NaN;
    lastCol = "";
    hFrom = hTo = hillshadeColors(mood);
    hCol.set(1);
    hEx.set(MOODS[mood].hillshade.exaggeration);
    hDir.set(MOODS[mood].hillshade.illuminationDirection);
    // Light arriving: the relief rises from flat (§D.6).
    hShown.set(0);
    if (hWanted) hShown.to(1, reduced() ? 160 : 1400, reduced() ? easeBy("linear") : easeBy("drift"));
    after();
    // The gap tiles the intro's fly and the first long flights cross are made now, in the DEM worker,
    // once the first frames are out (L2-04, L2-09).
    prewarmTimer = setTimeout(() => {
      prewarmTimer = null;
      if (destroyed) return;
      void prewarmDem({ signal: prewarmAc.signal }).then(() => {
        // then into MapLibre's DEM tile cache too, unless the opening is still playing (the controller
        // warms both sources after its "done" beat)
        const phase = getApp().phase;
        if (!destroyed && !prewarmAc.signal.aborted && phase !== "intro" && phase !== "loading")
          void prewarmTiles(map, { sources: [SOURCE_IDS.dem], zooms: [9, 10], bbox: REGION_BBOX, signal: prewarmAc.signal });
      });
    }, PREWARM_AFTER_MS);
  }
  const prewarmAc = new AbortController();
  let prewarmTimer: ReturnType<typeof setTimeout> | null = null;

  const offDem = on("dem:ready", () => attachDem());

  const api: Terrain = {
    get demAttached() {
      return demAttached;
    },

    setMood(m: Mood, durationMs: number): void {
      if (destroyed) return;
      mood = m;
      const h = MOODS[m].hillshade;
      const dur = Math.max(0, durationMs);
      // Colours, direction (not transitionable) and exaggeration move together on the ticker with
      // MapLibre's cubic in-out, written in steps (applyHillshade); at once under reduced motion (§E.10).
      hFrom = hillshadeMix(hFrom, hTo, hCol.v);
      hTo = hillshadeColors(m);
      const dirTo = hDir.v + turn(hDir.v, h.illuminationDirection);
      if (reduced() || dur === 0) {
        hCol.set(1);
        hDir.set(dirTo);
        hEx.set(h.exaggeration);
      } else {
        hCol.set(0);
        hCol.to(1, dur, cubicInOut);
        hDir.to(dirTo, dur, cubicInOut);
        hEx.to(h.exaggeration, dur, cubicInOut);
      }
      after();
    },

    setContoursVisible(v: boolean): void {
      if (destroyed) return;
      const r = reduced();
      if (v) cFlag.to(1, r ? 160 : 640, easeBy(r ? "linear" : "settle"));
      else cFlag.to(0, r ? 160 : 448, easeBy(r ? "linear" : "leave"));
      after();
    },

    setHillshadeVisible(v: boolean): void {
      if (destroyed) return;
      hWanted = v;
      if (!demAttached) return;
      const r = reduced();
      if (v) {
        setVisibility(LAYER_IDS.hillshade, true);
        hShown.to(1, r ? 160 : 640, easeBy(r ? "linear" : "settle"));
      } else {
        hShown.to(0, r ? 160 : 448, easeBy(r ? "linear" : "leave"), () => {
          if (!hWanted && !destroyed) setVisibility(LAYER_IDS.hillshade, false);
        });
      }
      after();
    },

    fadeContours(v: number, fo?: { duration?: number; ease?: EaseName }): Promise<boolean> {
      if (destroyed) return Promise.resolve(false);
      const target = Math.max(0, Math.min(1, v));
      const r = reduced();
      const dur = r ? Math.min(160, fo?.duration ?? 160) : Math.max(0, fo?.duration ?? 640);
      return new Promise((resolve) => {
        let settled = false;
        const finish = (ok: boolean) => {
          if (settled) return;
          settled = true;
          resolve(ok);
        };
        cFade.to(target, dur, easeBy(r ? "linear" : (fo?.ease ?? "drift")), () => finish(cFade.v === target));
        after();
      });
    },

    attachDem,

    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      offDem();
      prewarmAc.abort();
      if (prewarmTimer) clearTimeout(prewarmTimer);
      prewarmTimer = null;
      loopOff?.();
      loopOff = null;
      terrains.delete(map);
    },
  };

  terrains.set(map, api);
  // The DEM may already be in memory (a return visit, or the map mounted after Enter).
  attachDem();
  return api;
}
